import { useEffect, useState, useContext } from "react";
import { Dialog, Box, IconButton, Typography, TextField, Button, CircularProgress, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { startConversation, sendMessage } from "../../api/webchat";
import { LanguageContext } from "../../context/LanguageContext";

export default function AgentTestDialogMobile({ open, onClose, agent, tenantId }) {
  const { translate } = useContext(LanguageContext);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState("");
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!agent?.id || !tenantId) {
      setError("Missing agent or tenant context");
      return;
    }
    let cancelled = false;
    (async () => {
      setError("");
      setMessages([]);
      const key = `tm:test:${tenantId}:${agent.id}`;
      const cached = localStorage.getItem(key);
      if (cached) {
        if (!cancelled) setConversationId(cached);
        return;
      }
      try {
        const resp = await startConversation(tenantId, agent.id, { source: "AgentTestDialogMobile", resume: false });
        const cid = resp?.conversationId;
        if (cid) {
          try { localStorage.setItem(key, cid); } catch {}
          if (!cancelled) setConversationId(cid);
        } else if (!cancelled) {
          setError("Failed to start conversation");
        }
      } catch (e) {
        if (!cancelled) {
          const msg = e?.code === "agent_disabled" ? "Public chat disabled for this agent" : e?.message || "Failed to start conversation";
          setError(msg);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, tenantId, agent?.id]);

  const doSend = async () => {
    if (!input.trim() || !agent || !tenantId || !conversationId) return;
    const userText = input.trim();
    setInput("");
    setError("");
    setMessages((m) => [...m, { role: "user", content: userText }]);
    setLoading(true);
    try {
      const resp = await sendMessage({ tenantId, agentId: agent.id, conversationId, body: userText, imageUrls: [] });
      const reply = resp?.reply || (resp?.status === "queued_no_bot" ? "Bot is inactive for this conversation." : "…");
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Backend error" }]);
      setError(e?.message || "Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  const doReset = async () => {
    try {
      if (!tenantId || !agent?.id) return;
      setResetting(true);
      const key = `tm:test:${tenantId}:${agent.id}`;
      try { localStorage.removeItem(key); } catch {}
      setMessages([]);
      setConversationId(null);
      setError("");
      const resp = await startConversation(tenantId, agent.id, { source: "AgentTestDialogMobileReset", resume: false });
      const cid = resp?.conversationId;
      if (cid) { try { localStorage.setItem(key, cid); } catch {}; setConversationId(cid); } else { setError("Failed to start conversation"); }
    } catch (e) {
      setError(e?.message || "Failed to reset test session");
    } finally {
      setResetting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Box sx={{ display: "flex", alignItems: "center", p: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Typography sx={{ flex: 1, textAlign: "center", fontWeight: 700 }}>
          {(translate && translate("TestChat")) || "Test chat"}: {agent?.name}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", height: "calc(100% - 56px)" }}>
        <Box sx={{ flex: 1, overflowY: "auto", bgcolor: "background.default", borderRadius: 1, p: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}
          {!conversationId && !error && (
            <Typography variant="body2" color="text.secondary">Starting conversation…</Typography>
          )}
          {messages.map((m, i) => (
            <Box key={i} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" color={m.role === "user" ? "primary" : "secondary"}>
                {m.role === "user" ? (translate && translate("You")) || "You" : agent?.name || "Agent"}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{m.content}</Typography>
            </Box>
          ))}
          {loading && (
            <Box sx={{ textAlign: "center", mt: 2 }}><CircularProgress size={20} /></Box>
          )}
        </Box>
        <Box sx={{ mt: 1 }}>
          <TextField
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={(translate && translate("SendAMessage")) || "Send a message"}
            fullWidth
            onKeyDown={(e) => e.key === "Enter" && doSend()}
            disabled={!conversationId || loading}
            size="small"
          />
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Button onClick={doSend} disabled={!conversationId || loading} variant="contained" sx={{ flex: 1 }}>
              {(translate && translate("Send")) || "Send"}
            </Button>
            <Button onClick={doReset} disabled={!conversationId || resetting || loading} variant="outlined" sx={{ flex: 1 }}>
              {(translate && translate("Reset")) || "Reset"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
